// Zustand store — access token kept in memory + localStorage.
// M2 refactor 2026-04-22: shared-terminal kiosk.
//   user  = máy kiosk (role ∈ 'admin'|'kiosk'), persistent cho đến khi bấm "Đăng xuất máy".
//   l2Employee + l2Session = NV đang verify trên máy. Hết 4h hoặc force-expire -> clear.
// AC-AUTH: session L2 = 4h; refresh access KHÔNG kéo dài L2.
import { create } from 'zustand';

const LS_KEY = 'hct.auth';

function loadPersist() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch { return null; }
}
function savePersist(data) {
  if (data) localStorage.setItem(LS_KEY, JSON.stringify(data));
  else localStorage.removeItem(LS_KEY);
}

const initial = loadPersist() || {
  accessToken: null,
  refreshToken: null,
  user: null,             // máy kiosk/admin
  l2SessionId: null,
  l2ExpiresAt: null,
  l2Employee: null,       // NV hiện đang verify trên máy (null nếu chưa pick)
};

export const useAuthStore = create((set, get) => ({
  ...initial,

  setAuth: ({ accessToken, refreshToken, user }) => {
    const state = { ...get(), accessToken, refreshToken, user };
    savePersist(state);
    set(state);
  },

  /**
   * Mở phiên L2 cho 1 NV — truyền đầy đủ {sessionId, expiresAt, employee}.
   */
  setL2Session: ({ sessionId, expiresAt, employee }) => {
    const state = {
      ...get(),
      l2SessionId: sessionId,
      l2ExpiresAt: expiresAt,
      l2Employee: employee || get().l2Employee || null,
    };
    savePersist(state);
    set(state);
  },

  /** Thoát NV (nhưng máy vẫn login) — dùng khi NV bấm "Thoát NV" hoặc expire. */
  clearL2Session: () => {
    const state = { ...get(), l2SessionId: null, l2ExpiresAt: null, l2Employee: null };
    savePersist(state);
    set(state);
  },

  /** Trả về session ID nếu còn hạn, ngược lại null (và tự dọn). */
  getValidL2SessionId: () => {
    const { l2SessionId, l2ExpiresAt } = get();
    if (!l2SessionId || !l2ExpiresAt) return null;
    if (new Date(l2ExpiresAt) <= new Date()) {
      get().clearL2Session();
      return null;
    }
    return l2SessionId;
  },

  /** Helper: trả về employee đang active nếu session còn hạn. */
  getValidL2Employee: () => {
    const sid = get().getValidL2SessionId();
    if (!sid) return null;
    return get().l2Employee;
  },

  /** Đăng xuất máy — clear hết. */
  logout: () => {
    savePersist(null);
    set({
      accessToken: null, refreshToken: null, user: null,
      l2SessionId: null, l2ExpiresAt: null, l2Employee: null,
    });
  },

  // 2026-04-24 SEC (Phase 1) + M165 (2026-05-15): cross-tab single-flight.
  //
  // Vấn đề: multi-tab refresh race — backend rotate token A → revoke A + issue B.
  // Tab 2 (chưa biết) cũng call refresh với A → fail (token revoked) → silent drop.
  //
  // M120 (same-tab): module-local Promise _inflightRefresh.
  // M165 (cross-tab): navigator.locks.request('hct-refresh', ...) — Web Locks API
  //   atomic acquire lock giữa các tabs cùng origin. Tab thứ 2 await lock; khi vào
  //   được lock, read localStorage để dùng token mới (đã được tab 1 update).
  //
  // Fallback: nếu trình duyệt không có Web Locks API (rất hiếm trên kiosk Chrome
  // hiện đại), dùng same-tab single-flight cũ.
  refreshAccess: async () => {
    const rt = get().refreshToken;
    if (!rt) return false;

    // Cross-tab: navigator.locks (Chrome 69+, Edge 79+, FF 96+, Safari 15.4+)
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request('hct-refresh', async () => {
        // Sau khi acquire lock có thể tab khác đã refresh xong — re-read localStorage
        const fresh = loadPersist();
        const currentRT = get().refreshToken;
        if (fresh?.refreshToken && fresh.refreshToken !== currentRT && fresh.refreshToken !== rt) {
          // Token đã được tab khác refresh — sync vào state local
          set({ ...get(), accessToken: fresh.accessToken, refreshToken: fresh.refreshToken });
          return true;
        }
        return _doRefresh(get, set, fresh?.refreshToken || rt);
      });
    }

    // Fallback same-tab single-flight (M120 cũ)
    if (_inflightRefresh) return _inflightRefresh;
    _inflightRefresh = _doRefresh(get, set, rt);
    try { return await _inflightRefresh; }
    finally { setTimeout(() => { _inflightRefresh = null; }, 0); }
  },
}));

// Module-local single-flight token (fallback khi không có Web Locks API).
let _inflightRefresh = null;

/**
 * Helper thực hiện POST /auth/refresh thật, update state + localStorage.
 * Tách ra để cả Web Locks path và fallback đều dùng chung.
 */
async function _doRefresh(get, set, rt) {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const newState = { ...get(), accessToken: data.access_token, refreshToken: data.refresh_token };
    savePersist(newState);
    set(newState);
    return true;
  } catch { return false; }
}
